/** @file Handles the email form for new login and registration. */
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import * as form from '@/components/Form';
import { clsInput } from './cls';
/** Form for entering an email address to begin the login flow. */
export const EmailForm = tsx.component({
    name: 'login-email-form',
    props: {
        submit: p.ofType().required,
        description: p.ofType().optional,
        showPrivacyPolicy: p(Boolean).default(false),
    },
    /** @returns State of the component. */
    data() {
        return {
            inputValue: Maybe.empty(),
            privacyPolicyAccepted: false,
        };
    },
    computed: {
        /** @returns The checkbox option data for the privacy policy acceptance field. */
        privacyPolicyOptions() {
            return [
                {
                    tag: 'option',
                    value: 'accepted',
                    label: (<span>
                            I have read and agree with CodeGrade's{' '}
                            <a href="https://www.codegrade.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                                privacy policy
                            </a>
                            .
                        </span>),
                },
            ];
        },
        /** @returns A disabled reason tooltip when the privacy policy has not been accepted, or `undefined`. */
        submitDisabled() {
            if (this.showPrivacyPolicy && !this.privacyPolicyAccepted) {
                return {
                    type: 'tooltip',
                    reason: 'Please read and accept the privacy policy before continuing.',
                };
            }
            return undefined;
        },
    },
    methods: {
        /**
         * Handles input changes to the email form.
         *
         * @param raw The raw input value from the user.
         */
        handleInput(raw) {
            this.inputValue = Maybe.of(raw);
        },
    },
    /** @returns The rendered email form JSX. */
    render() {
        return (<form.Root onBeforeSubmit={() => {
                // Trigger validation before submitting.
                this.inputValue = this.inputValue.alt(Maybe.of(''));
            }} handleSubmit={async () => {
                if (this.showPrivacyPolicy && !this.privacyPolicyAccepted) {
                    throw new Error('Please read and accept the privacy policy');
                }
                return await this.submit(this.inputValue.orDefault(''));
            }} submitDisabledReason={this.submitDisabled} submitLoadingMessage="Submitting request" submitSuccessMessage="Received response" submitLabel="Continue" submitOnEnter actionBarVariant="wide-submit" validationOptions={{
                validationErrorMessage: 'Please enter a valid email address to continue',
            }}>
                <form.Field label="Email Address">
                    <form.Description class={this.description?.class ?? 'sr-only'}>
                        {this.description?.text ?? 'Enter your email address to login'}
                    </form.Description>
                    <form.Input class={clsInput} type="email" autocomplete="email" value={this.inputValue.extract()} onInput={this.handleInput} required v-focus/>
                </form.Field>

                {this.showPrivacyPolicy && (<form.Field>
                        <form.Checkboxes data-cy="privacy-policy-accepted" name="privacy-policy" legend="Privacy policy" legendClasses="sr-only" options={this.privacyPolicyOptions} value={this.privacyPolicyAccepted ? new Set(['accepted']) : new Set()} onInput={value => {
                    this.privacyPolicyAccepted = value.has('accepted');
                }}/>
                    </form.Field>)}
            </form.Root>);
    },
});
